import React, { useEffect, useState, useCallback } from "react";
import ExperienceEditModal from "./ExperienceEditModal";
import Toggle from "./Toggle";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ExperiencesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editSlug, setEditSlug] = useState(null);
  const [flash, setFlash] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/experiences`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setRows(data);
      setErr(null);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const patchRow = async (slug, patch) => {
    const r = await fetch(`${API}/api/admin/experiences/${slug}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!r.ok) {
      setFlash({ type: "err", msg: `Update failed: HTTP ${r.status}` });
      return;
    }
    setFlash({ type: "ok", msg: "Saved." });
    refresh();
  };

  const deleteRow = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This removes the DB row AND its image folder.`)) return;
    const typed = window.prompt(`Type the slug "${slug}" to confirm deletion:`);
    if (typed !== slug) {
      setFlash({ type: "err", msg: "Slug did not match — canceled." });
      return;
    }
    const r = await fetch(`${API}/api/admin/experiences/${slug}`, {
      method: "DELETE",
      credentials: "include",
      headers: { "X-Confirm-Delete": slug },
    });
    if (!r.ok) {
      setFlash({ type: "err", msg: `Delete failed: HTTP ${r.status}` });
      return;
    }
    setFlash({ type: "ok", msg: `Deleted ${slug}.` });
    refresh();
  };

  return (
    <section data-testid="admin-experiences-tab">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h2 className="font-display text-3xl text-charcoal">Experiences</h2>
        <div className="flex gap-3 flex-wrap">
          <a
            href={`${API}/api/admin/experience-template.pptx`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-download-template"
            className="eyebrow border border-charcoal text-charcoal px-3 py-2 hover:bg-charcoal hover:text-ivory transition text-xs"
          >
            ↓ Template
          </a>
          <label
            data-testid="admin-upload-template-btn"
            className="eyebrow border border-charcoal text-charcoal px-3 py-2 hover:bg-charcoal hover:text-ivory transition text-xs cursor-pointer"
          >
            ↑ Fill & Upload
            <input
              type="file"
              accept=".pptx"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const form = new FormData();
                form.append("file", f);
                const r = await fetch(`${API}/api/admin/experiences/upload-template`, {
                  method: "POST",
                  credentials: "include",
                  body: form,
                });
                if (!r.ok) {
                  setFlash({ type: "err", msg: `Upload failed: HTTP ${r.status}` });
                  return;
                }
                const d = await r.json();
                setFlash({ type: "ok", msg: `Draft created: ${d.slug}` });
                refresh();
              }}
            />
          </label>
          <label
            data-testid="admin-upload-ai-btn"
            className="eyebrow border border-brass text-brass px-3 py-2 hover:bg-brass hover:text-ivory transition text-xs cursor-pointer"
          >
            ⚡ AI ingest (PPTX/PDF)
            <input
              type="file"
              accept=".pptx,.pdf"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setFlash({ type: "ok", msg: "AI is thinking… (10-60s)" });
                const form = new FormData();
                form.append("file", f);
                const r = await fetch(`${API}/api/admin/experiences/ingest-ai`, {
                  method: "POST",
                  credentials: "include",
                  body: form,
                });
                if (!r.ok) {
                  const err = await r.text();
                  setFlash({ type: "err", msg: `AI ingest failed: ${err.slice(0, 200)}` });
                  return;
                }
                const d = await r.json();
                setFlash({ type: "ok", msg: `AI extracted: ${d.slug} (${d.images_saved} imgs). Review in draft.` });
                refresh();
                setEditSlug(d.slug);
              }}
            />
          </label>
          <button
            type="button"
            onClick={() => setEditSlug("")}
            data-testid="admin-new-experience-btn"
            className="eyebrow bg-charcoal text-ivory px-4 py-2 hover:bg-charcoal/90 transition"
          >
            + New
          </button>
        </div>
      </div>

      {flash && (
        <div
          className={`mb-4 px-4 py-3 text-sm ${flash.type === "ok" ? "bg-brass/10 text-brass" : "bg-red-50 text-red-800"}`}
          data-testid="admin-flash"
        >
          {flash.msg}
        </div>
      )}

      {err && <p className="text-red-700 italic-quote">Error: {err}</p>}
      {loading && <p className="text-charcoal/60 italic-quote">Loading…</p>}

      {!loading && rows.length > 0 && (
        <div className="overflow-x-auto border border-mist bg-white">
          <table className="w-full text-sm">
            <thead className="bg-mist/60 text-charcoal">
              <tr>
                <th className="text-left px-4 py-3 eyebrow">Image</th>
                <th className="text-left px-4 py-3 eyebrow">Title</th>
                <th className="text-left px-4 py-3 eyebrow">Where</th>
                <th className="text-left px-4 py-3 eyebrow">Price</th>
                <th className="text-left px-4 py-3 eyebrow">Visible</th>
                <th className="text-left px-4 py-3 eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.slug} data-testid={`admin-exp-row-${r.slug}`} className="border-t border-mist">
                  <td className="px-4 py-3">
                    {r.hero_image ? (
                      <img src={r.hero_image} alt="" className="w-14 h-10 object-cover" loading="lazy" />
                    ) : (
                      <div className="w-14 h-10 bg-mist" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-display text-charcoal">{r.title || <em className="text-charcoal/50">(untitled)</em>}</div>
                    <div className="text-charcoal/50 text-xs">{r.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-charcoal/70">{r.city || "—"} · {r.country || "—"}</td>
                  <td className="px-4 py-3 text-charcoal/70"><PriceCell row={r} /></td>
                  <td className="px-4 py-3">
                    <Toggle
                      on={!!r.visible}
                      onChange={(v) => patchRow(r.slug, { visible: v })}
                      testid={`admin-exp-visible-${r.slug}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setEditSlug(r.slug)}
                      data-testid={`admin-exp-edit-${r.slug}`}
                      className="eyebrow text-brass hover:underline mr-4"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteRow(r.slug)}
                      data-testid={`admin-exp-delete-${r.slug}`}
                      className="eyebrow text-red-700 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editSlug !== null && (
        <ExperienceEditModal
          slug={editSlug}
          onClose={() => setEditSlug(null)}
          onSaved={(info) => {
            const created = info && info.created;
            const keepOpen = info && info.keepOpen;
            if (created) {
              // Just persisted a brand-new row. Keep the modal open in edit
              // mode (the modal transitioned itself) so admin can upload
              // images and finalize; sync the parent list + slug tracker.
              setEditSlug(info.slug);
              setFlash({ type: "ok", msg: `Created ${info.slug}. Upload images below.` });
            } else if (!keepOpen) {
              setEditSlug(null);
              setFlash({ type: "ok", msg: "Saved." });
            }
            refresh();
          }}
        />
      )}
    </section>
  );
}

function PriceCell({ row }) {
  if (row.price_unit === "on_request") return <span className="italic-quote">On request</span>;
  if (row.price_unit === "per_person" && row.price != null) return <span>€{fmt(row.price)}/pax</span>;
  if (row.price_unit === "per_group" && Array.isArray(row.price_tiers) && row.price_tiers.length > 0) {
    const t0 = row.price_tiers[0];
    return <span>from €{Math.round(t0.price / t0.max_pax)}/pax · group</span>;
  }
  if (row.price != null) return <span>€{fmt(row.price)}</span>;
  return <span className="text-charcoal/40">—</span>;
}
function fmt(n) {
  const x = Number(n);
  return Number.isInteger(x) ? String(x) : x.toFixed(0);
}
